import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, AlertCircle, CheckCircle2, XCircle, ShieldCheck, Pill, Receipt } from "lucide-react";
import { toast } from "sonner";
import { confirm as brandedConfirm } from "@/components/ui/confirm-dialog";

/**
 * Patient-facing page reached from the change-request email/SMS.
 * Shows the original vs prescribed strength, the dollar delta, and lets the
 * patient Accept (releases ERX) or Decline (cancels + queues refund).
 */
export default function ReviewChangeRequest() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await API.get(`/orders/${orderId}/change-request`);
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load request");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orderId]);

  const accept = async () => {
    if (!(await brandedConfirm({ title: 'Are you sure?', description: "Accept the new strength and release your prescription to the pharmacy?", confirmText: 'Confirm', cancelText: 'Cancel', tone: 'info' }))) return;
    setActing(true);
    try {
      await API.post(`/orders/${orderId}/change-request/accept`);
      toast.success("Accepted — your prescription is on its way");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to accept");
    } finally {
      setActing(false);
    }
  };

  const decline = async () => {
    if (!(await brandedConfirm({ title: 'Are you sure?', description: `Decline this change? You'll receive a refund minus the $${data?.consult_fee_if_declined ?? 25} consultation fee.`, confirmText: 'Confirm', cancelText: 'Cancel', tone: 'info' }))) return;
    setActing(true);
    try {
      const r = await API.post(`/orders/${orderId}/change-request/decline`);
      toast.success(`Declined — refund of $${r.data?.refund_amount_usd ?? 0} queued`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to decline");
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }
  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl border border-red-200 p-6 text-center" data-testid="review-change-error">
          <AlertCircle className="w-10 h-10 mx-auto text-red-500 mb-3" />
          <h1 className="text-xl font-semibold mb-2">We couldn&apos;t load this request</h1>
          <p className="text-slate-600 mb-4">{err}</p>
          <Button asChild variant="outline"><Link to="/dashboard">Back to dashboard</Link></Button>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const status = data.change_status || "none";
  const delta = data.cost_delta_usd ?? null;
  const deltaLabel = delta == null ? "—" : delta > 0 ? `+$${delta}` : `-$${Math.abs(delta)}`;
  const deltaCls = delta == null ? "text-slate-500" : delta > 0 ? "text-amber-700 bg-amber-50 border-amber-200" : "text-emerald-700 bg-emerald-50 border-emerald-200";

  if (status !== "pending_patient_approval") {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-purple-50 to-violet-100">
        <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 p-6 text-center" data-testid="review-change-resolved">
          {status === "accepted" ? (
            <>
              <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500 mb-3" />
              <h1 className="text-xl font-semibold mb-2">You&apos;ve already accepted this change</h1>
              <p className="text-slate-600 mb-4">Your pharmacy is processing your prescription. You can track its status from your dashboard.</p>
            </>
          ) : status === "declined" ? (
            <>
              <XCircle className="w-10 h-10 mx-auto text-slate-500 mb-3" />
              <h1 className="text-xl font-semibold mb-2">You declined this change</h1>
              <p className="text-slate-600 mb-4">Our team is processing your refund. You should see it on your card within 1–2 business days.</p>
            </>
          ) : (
            <>
              <ShieldCheck className="w-10 h-10 mx-auto text-purple-500 mb-3" />
              <h1 className="text-xl font-semibold mb-2">No pending changes on this order</h1>
              <p className="text-slate-600 mb-4">Everything is up to date on our end.</p>
            </>
          )}
          <Button onClick={() => navigate("/dashboard")} className="bg-purple-600 hover:bg-purple-700 text-white">Go to dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-violet-50 px-4 py-12">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl border border-purple-200 shadow-sm p-7" data-testid="review-change-card">
          <div className="flex items-center gap-2 text-purple-700 text-sm font-semibold uppercase tracking-wider mb-2">
            <ShieldCheck className="w-4 h-4" /> Action required
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Your prescriber recommended a change</h1>
          <p className="text-slate-600 mt-2">
            Before we can transmit your prescription to the pharmacy, please review the
            change below and let us know how you&apos;d like to proceed.
          </p>

          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            <div className="rounded-xl border border-slate-200 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">You requested</p>
              <p className="font-semibold text-slate-900 mt-1">{data.medication_name}</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{data.requested_strength || "—"}</p>
              {data.old_price_usd != null && <p className="text-sm text-slate-500 mt-1">${data.old_price_usd}</p>}
            </div>
            <div className="rounded-xl border-2 border-purple-300 bg-purple-50 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-purple-700 flex items-center gap-1"><Pill className="w-3 h-3" /> Prescriber recommends</p>
              <p className="font-semibold text-slate-900 mt-1">{data.medication_name}</p>
              <p className="text-2xl font-bold text-purple-800 mt-1">{data.prescribed_strength || "—"}</p>
              {data.new_price_usd != null && <p className="text-sm text-purple-600 mt-1">${data.new_price_usd}</p>}
            </div>
          </div>

          <div className={`mt-4 rounded-xl border ${deltaCls} px-4 py-3 flex items-center gap-2`}>
            <Receipt className="w-4 h-4 shrink-0" />
            <p className="text-sm font-semibold">
              Cost change: <span className="font-bold">{deltaLabel}</span>
              {delta != null && delta > 0 && <span className="font-normal"> — additional charge</span>}
              {delta != null && delta < 0 && <span className="font-normal"> — refund to your card</span>}
            </p>
          </div>

          <div className="mt-6 rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-700">
            <p className="font-semibold mb-1">If you decline</p>
            <p>You&apos;ll receive a refund of your original payment minus the
            <b> ${data.consult_fee_if_declined} </b> consultation fee. Your provider review has already taken place,
            so we retain that fee per our terms.</p>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Button
              onClick={accept}
              disabled={acting}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="accept-change-btn"
            >
              {acting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Accept & send to pharmacy
            </Button>
            <Button
              onClick={decline}
              disabled={acting}
              variant="outline"
              className="flex-1 border-slate-300 text-slate-700 hover:bg-slate-50"
              data-testid="decline-change-btn"
            >
              {acting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
              Decline & request refund
            </Button>
          </div>

          <p className="text-xs text-slate-500 mt-4 text-center">
            Questions? Reply to the email we sent you or message us from your dashboard.
          </p>
        </div>
      </div>
    </div>
  );
}
